import { Component, effect, inject, LOCALE_ID, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MessageService, SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { RegisterPayload } from '../../models/auth.model';
import { Auth } from '../../services/auth';
import { translateCouponErrorMessage } from '../../utils/coupon-error-messages';
import { WithdrawalService } from '../../services/withdrawal.service';

@Component({
  selector: 'app-signup',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    ButtonModule,
    InputIconModule,
    IconFieldModule,
    RouterLink,
    SelectButtonModule,
    CheckboxModule,
    TooltipModule,
  ],
  templateUrl: './signup.html',
  styleUrl: './signup.css',
  standalone: true,
})
export class Signup {
  private authService = inject(Auth);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private localeId = inject(LOCALE_ID);
  private withdrawalService = inject(WithdrawalService);

  openWithdrawalDialog(): void {
    this.withdrawalService.openDialog();
  }
  passwordVisible = signal(false);
  registrationType = signal<'practice' | 'patient'>('practice');

  form = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
    practiceName: new FormControl('', [Validators.required]),
    agbAccepted: new FormControl<boolean>(false, [Validators.requiredTrue]),
    newsletterConsent: new FormControl<boolean>(false, []),
    couponCode: new FormControl('', []),
  });

  accountTypeOptions = signal<SelectItem<string>[]>([
    { label: $localize`Practice`, value: 'practice' },
    { label: $localize`Patient`, value: 'patient' },
  ]);

  constructor() {
    // Update practiceName validation based on registration type
    effect(() => {
      const type = this.registrationType();
      const practiceNameControl = this.form.get('practiceName');
      if (type === 'practice') {
        practiceNameControl?.setValidators([Validators.required]);
      } else {
        practiceNameControl?.clearValidators();
        practiceNameControl?.setValue('');
      }
      practiceNameControl?.updateValueAndValidity({ emitEvent: false });
    });
  }

  onRegister() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: $localize`Missing fields`,
        detail: $localize`Please fill in all required fields`,
      });
      return;
    }

    const payload: RegisterPayload = {
      firstName: this.form.value.firstName!,
      lastName: this.form.value.lastName!,
      email: this.form.value.email!,
      password: this.form.value.password!,
      practiceName:
        this.registrationType() === 'practice'
          ? this.form.value.practiceName!
          : undefined,
      newsletterConsent: this.form.value.newsletterConsent ?? false,
      locale: this.localeId,
      couponCode:
        this.registrationType() === 'patient' &&
        this.form.value.couponCode?.trim()
          ? this.form.value.couponCode.trim()
          : undefined,
    };

    this.authService.register(payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: $localize`Sign up successful`,
          detail: $localize`Please check your email to verify your account before signing in.`,
        });
        this.router.navigate(['/signin']);
      },
      error: (error) => {
        if (error.status === 409) {
          if (error?.error?.message === 'User already exists') {
            this.messageService.add({
              severity: 'error',
              summary: $localize`Already exists`,
              detail: $localize`Email address already in use.`,
            });
            return;
          }

          this.messageService.add({
            severity: 'error',
            summary: $localize`:@@complimentary.invalidCoupon:Invalid coupon`,
            detail: translateCouponErrorMessage(error?.error?.message),
          });
          return;
        }
        if (error.status === 422) {
          this.messageService.add({
            severity: 'error',
            summary: $localize`:@@complimentary.invalidCoupon:Invalid coupon`,
            detail: translateCouponErrorMessage(error?.error?.message),
          });
          return;
        }
        this.messageService.add({
          severity: 'error',
          summary: $localize`Error`,
          detail: $localize`An error occurred while registering. Please try again later.`,
        });
      },
    });
  }

  togglePassword() {
    this.passwordVisible.update((v) => !v);
  }
}
